import { Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { storePetDetScreenDetails } from "../../../redux/reducers/mixedSlice";
import {
  getAppointmentsByPetId,
  getPetAndAppointmentDetailsById,
  getPetDetailsByIdNew,
  getProceduresByPetIdWoLoader,
  getReminderProceduresByPetIdWoLoader,
} from "../../../redux/reducers/petSlice";
import CommonClinicPetDetails from "../../CommonScreens/CommonClinicPetDetails/CommonClinicPetDetails";
import BillingHistory from "./Billing/BillingHistory";
import {
  getBaseFetchKey,
  getOverviewFetchKey,
  setBaseFetchKey,
  setOverviewFetchKey,
} from "./clinicPetDetailsSession";
import MedicalHistoryNew from "./MedicalHistory/MedicalHistoryNew";
import Overview from "./Overview/Overview";
import PatientWorkbook from "./PatientWorkbook/PatientWorkbook";
import Task from "./Task/Task";

export const PET_DETAIL_TAB_PATHS = {
  overView: "overview",
  treatmentPlan: "treatment-plan",
  ioMonitoring: "io-monitoring",
  patientWorkbook: "patient-workbook",
  billing: "billing",
  history: "history",
  task: "task",
  discharge: "discharge",
};

export const PET_DETAIL_PATH_TO_TAB = {
  overview: "overView",
  "treatment-plan": "treatmentPlan",
  "io-monitoring": "ioMonitoring",
  "patient-workbook": "patientWorkbook",
  billing: "billing",
  history: "history",
  task: "task",
  discharge: "discharge",
};

export const ClinicPetDetailsRedirect = () => {
  const location = useLocation();
  const tabFromState = location.state?.selectedTab;
  const path =
    PET_DETAIL_TAB_PATHS[tabFromState] ?? PET_DETAIL_TAB_PATHS.overView;
  const nextState = {
    ...(location.state || {}),
    selectedTab: PET_DETAIL_PATH_TO_TAB[path] ?? "overView",
  };
  return (
    <Navigate to={`/clinic-pet-details/${path}`} replace state={nextState} />
  );
};

const ClinicPetDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab } = useParams();
  const dispatch = useDispatch();
  const { title, type, appointment } = location.state || {};
  const selectedTab = PET_DETAIL_PATH_TO_TAB[tab];
  const [isVideoCallEnabled, setVideoCallEnabled] = useState(false);
  const [scrollable, setScrollable] = useState(true);
  const [isEditBill, setEditBill] = useState(
    location?.state?.editBill ?? false,
  );
  const [allergyModOpen, setAllergyModOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(
    location?.state?.bill ?? null,
  );
  const [inpatientId, setInpatientId] = useState(location?.state?.inpatientId);
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const appointmentId = appointment?.appoinment?.appointmentId;
  const petId = appointment?.appoinment?.petId;
  const reqApnt = petAndApntData?.appointment ?? {};
  const hasMatchingBaseDetails = (() => {
    if (!petId || !petAndApntData) return false;
    const loadedPetId =
      petAndApntData?.pet?.petId ?? petAndApntData?.appointment?.petId;
    const loadedApntId = petAndApntData?.appointment?.appointmentId;
    if (loadedPetId !== petId) return false;
    if (appointmentId && loadedApntId !== appointmentId) return false;
    return true;
  })();
  const [isBaseDetailsLoaded, setIsBaseDetailsLoaded] = useState(
    hasMatchingBaseDetails,
  );

  useEffect(() => {
    dispatch(
      storePetDetScreenDetails({
        appointmentId: appointmentId ?? null,
        petId: appointmentId ? null : petId,
      }),
    );
  }, [appointmentId, petId, dispatch]);

  // Call base API only when pet/appointment changes — not on tab remounts.
  useEffect(() => {
    if (!petId) return;
    const fetchKey = `${petId}:${appointmentId || ""}`;

    if (getBaseFetchKey() === fetchKey || hasMatchingBaseDetails) {
      setBaseFetchKey(fetchKey);
      setIsBaseDetailsLoaded(true);
      return;
    }

    setBaseFetchKey(fetchKey);
    setIsBaseDetailsLoaded(false);
    dispatch(
      getPetAndAppointmentDetailsById({
        petId,
        url: `${appointmentId ? `?appointmentId=${appointmentId}` : ""}`,
      }),
    ).then((res) => {
      if (res?.payload) {
        setIsBaseDetailsLoaded(true);
      }
    });
  }, [appointmentId, petId, dispatch, hasMatchingBaseDetails]);

  // Overview-only fetches: once per pet after base data is ready (survives tab remounts).
  useEffect(() => {
    if (selectedTab !== "overView" || !petId || !isBaseDetailsLoaded) return;
    const fetchKey = `${petId}:${appointmentId || ""}`;
    if (getOverviewFetchKey() === fetchKey) return;
    setOverviewFetchKey(fetchKey);
    dispatch(getAppointmentsByPetId(petId));
    dispatch(getPetDetailsByIdNew(petId));
    dispatch(
      getProceduresByPetIdWoLoader({
        petId,
        url: `?reminderPreventive=true&page=1&itemSize=50`,
      }),
    );
    dispatch(
      getReminderProceduresByPetIdWoLoader({
        petId,
        url: `?reminderProcedure=true&page=1&itemSize=50`,
      }),
    );
  }, [selectedTab, petId, appointmentId, dispatch, isBaseDetailsLoaded]);

  // Only hydrate bill from location.state when present (never wipe local bill set by children).
  useEffect(() => {
    const { bill, editBill: editBillFromNav } = location.state || {};
    if (bill) {
      setEditBill(editBillFromNav ?? false);
      setSelectedBill(bill);
    }
  }, [location.state?.bill, location.state?.editBill]);

  const handleTabNavigation = (tabId, { resetBilling = false } = {}) => {
    if (!tabId) return;

    const nextState = { ...(location.state || {}), selectedTab: tabId };

    if (tabId === "billing" && resetBilling) {
      setEditBill(false);
      setSelectedBill(null);
      delete nextState.bill;
      nextState.editBill = false;
    } else if (tabId === "history") {
      setEditBill(false);
      nextState.editBill = false;
    }

    // Same tab: only re-navigate when resetting billing edit mode.
    if (tabId === selectedTab && !(tabId === "billing" && resetBilling)) {
      return;
    }

    const path = PET_DETAIL_TAB_PATHS[tabId] ?? PET_DETAIL_TAB_PATHS.overView;
    navigate(`/clinic-pet-details/${path}`, {
      replace: tabId === selectedTab,
      state: nextState,
    });
  };

  // Keep child APIs that call setSelectedTab("billing") working via navigate.
  // Do not reset billing here — children often setSelectedBill first, then switch tab.
  const setSelectedTab = (tabIdOrUpdater) => {
    const nextTab =
      typeof tabIdOrUpdater === "function"
        ? tabIdOrUpdater(selectedTab)
        : tabIdOrUpdater;
    handleTabNavigation(nextTab, { resetBilling: false });
  };

  if (!selectedTab) {
    return (
      <Navigate
        to={`/clinic-pet-details/${PET_DETAIL_TAB_PATHS.overView}`}
        replace
        state={location.state}
      />
    );
  }

  if (
    selectedTab === "treatmentPlan" ||
    selectedTab === "ioMonitoring" ||
    selectedTab === "discharge"
  ) {
    return (
      <Navigate
        to={`/clinic-pet-details/${PET_DETAIL_TAB_PATHS.patientWorkbook}`}
        replace
        state={{ ...(location.state || {}), selectedTab: "patientWorkbook" }}
      />
    );
  }

  const showPatientWorkbook =
    reqApnt?.appointmentId &&
    // reqApnt?.appointmentStatus?.toLowerCase() === "completed" &&
    (!reqApnt?.lock ||
      petAndApntData?.inpatientResponse?.patientId ||
      ["consultation", "finalize", "completed"].includes(
        reqApnt?.appointmentStatus?.toLowerCase(),
      ) ||
      (reqApnt?.appointmentType === "Virtual" &&
        reqApnt?.appointmentStatus?.toLowerCase() === "inprogress"));

  const petDetailTabs = [
    { id: "overView", label: "Overview" },
    {
      id: "patientWorkbook",
      label: "Patient Workbook",
      // Keep active route tab visible even before appointment status loads
      visible: !!showPatientWorkbook || selectedTab === "patientWorkbook",
    },
    { id: "billing", label: "Billing" },
    { id: "history", label: "History" },
    // {
    //   id: "task",
    //   label: "Task",
    //   visible: !!apntDet?.bundleId || selectedTab === "task",
    // },
  ];

  const getTabClassName = (tabId) =>
    `flex-center cursor ${
      selectedTab === tabId ? "pet-det-sel-tab" : "pet-det-un-sel-tab"
    }`;

  return (
    <CommonClinicPetDetails
      upcomingVisible={selectedTab === "overView"}
      isVideoCallEnabled={isVideoCallEnabled}
      isBaseDetailsLoaded={isBaseDetailsLoaded}
      setVideoCallEnabled={setVideoCallEnabled}
      appointment={appointment}
      scrollable={scrollable}
      setSelectedTab={setSelectedTab}
      setInpatientId={setInpatientId}
      allergyModOpen={allergyModOpen}
      setAllergyModOpen={setAllergyModOpen}
      selectedTab={selectedTab}
      setScrollable={setScrollable}
    >
      <Grid container className="ph2">
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          className="back-white p10 bs-margin"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row">
                {petDetailTabs
                  .filter((tabItem) => tabItem.visible !== false)
                  .map((tabItem) => (
                    <div
                      key={tabItem.id}
                      onClick={() =>
                        handleTabNavigation(tabItem.id, {
                          resetBilling: tabItem.id === "billing",
                        })
                      }
                    >
                      <div className={getTabClassName(tabItem.id)}>
                        {tabItem.label}
                      </div>
                    </div>
                  ))}
              </div>
            </Grid>
          </Grid>
        </Grid>
        {selectedTab === "overView" ? (
          <Overview
            appointment={appointment}
            title={title != null && title !== undefined ? title : type}
            isBaseDetailsLoaded={isBaseDetailsLoaded}
            isVideoCallEnabled={isVideoCallEnabled}
            setVideoCallEnabled={setVideoCallEnabled}
            allergyModOpen={allergyModOpen}
            setAllergyModOpen={setAllergyModOpen}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
        ) : null}
        {selectedTab === "patientWorkbook" ? (
          <PatientWorkbook
            appointment={appointment}
            setScrollable={setScrollable}
            setSelectedBill={setSelectedBill}
            setSelectedTab={setSelectedTab}
            setEditBill={setEditBill}
          />
        ) : null}
        <div className="mtMin20 w100Per">
          {selectedTab === "billing" ? (
            <BillingHistory
              isEditBill={isEditBill}
              setEditBill={setEditBill}
              petId={appointment?.appoinment?.petId}
              selectedBill={selectedBill}
              setSelectedBill={setSelectedBill}
              apntId={appointment?.appoinment?.appointmentId}
            />
          ) : null}
        </div>
        {selectedTab === "history" ? (
          <MedicalHistoryNew
            petId={appointment?.appoinment?.petId}
            apntId={appointment?.appoinment?.appointmentId ?? null}
          />
        ) : null}
        {selectedTab === "task" ? <Task /> : null}
      </Grid>
    </CommonClinicPetDetails>
  );
};

export default ClinicPetDetails;
